import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { getDashboardStats } from '../services/api';
import { ArrowUpRight, ArrowDownRight, Package, AlertTriangle, Zap, ShoppingCart, Plus, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCachedData } from '../hooks/useDataCache';
import { CardSkeleton, TableSkeleton } from '../components/Skeletons';

const statConfig = [
    { key: 'total_products', label: 'Toplam Urun', icon: Package, gradient: 'from-blue-500 to-blue-600', lightBg: 'bg-blue-50', lightText: 'text-blue-600' },
    { key: 'total_warehouses', label: 'Depolar', icon: Package, gradient: 'from-emerald-500 to-emerald-600', lightBg: 'bg-emerald-50', lightText: 'text-emerald-600' },
    { key: 'total_movements', label: 'Stok Hareketleri', icon: TrendingUp, gradient: 'from-violet-500 to-violet-600', lightBg: 'bg-violet-50', lightText: 'text-violet-600' },
    { key: 'low_stock_count', label: 'Dusuk Stok', icon: AlertTriangle, gradient: 'from-amber-500 to-orange-500', lightBg: 'bg-amber-50', lightText: 'text-amber-600' },
];

const quickActions = [
    { label: 'Hizli Stok Girisi', icon: Zap, to: '/fast-stock', color: 'bg-[#2563eb] text-white hover:bg-[#1d4ed8]' },
    { label: 'Yeni Siparis', icon: ShoppingCart, to: '/sales', color: 'bg-blue-50 text-blue-700 hover:bg-blue-100' },
    { label: 'Urun Ekle', icon: Plus, to: '/products', color: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' },
];

export default function Dashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const fetcher = useCallback(() => getDashboardStats(), []);
    const { data: cachedStats } = useCachedData('dashboard', fetcher);

    useEffect(() => {
        if (cachedStats) { setStats(cachedStats); setLoading(false); }
    }, [cachedStats]);

    if (loading) return (
        <DashboardLayout>
            <div className="space-y-6">
                <CardSkeleton count={4} />
                <TableSkeleton rows={5} cols={4} />
            </div>
        </DashboardLayout>
    );

    return (
        <DashboardLayout>
            <div className="space-y-8" data-testid="dashboard">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="page-title">Dashboard</h1>
                        <p className="page-subtitle">Genel bakis</p>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="flex flex-wrap gap-2" data-testid="quick-actions">
                    {quickActions.map(a => (
                        <button key={a.to} onClick={() => navigate(a.to)}
                            className={`btn ${a.color} text-sm gap-2`}
                            data-testid={`quick-${a.label.toLowerCase().replace(/\s+/g, '-')}`}>
                            <a.icon size={16} />
                            {a.label}
                        </button>
                    ))}
                </div>

                {/* Stat Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stat-cards">
                    {statConfig.map(s => {
                        const Icon = s.icon;
                        const value = stats?.[s.key] ?? 0;
                        const isWarning = s.key === 'low_stock_count' && value > 0;
                        return (
                            <div key={s.key} className="card group cursor-default" data-testid={`stat-${s.key}`}>
                                <div className="flex items-start justify-between mb-3">
                                    <div className={`w-10 h-10 rounded-xl ${s.lightBg} flex items-center justify-center`}>
                                        <Icon size={20} className={s.lightText} />
                                    </div>
                                    {isWarning && <span className="badge badge-warning text-[10px]">Dikkat</span>}
                                </div>
                                <div className="text-2xl font-bold text-gray-900">{value}</div>
                                <div className="text-xs text-gray-400 mt-1 font-medium">{s.label}</div>
                            </div>
                        );
                    })}
                </div>

                {/* Recent Movements */}
                <div className="data-card" data-testid="recent-movements">
                    <div className="data-card-header">
                        <div className="data-card-title">
                            <span className="dot green" /> Son Stok Hareketleri
                        </div>
                        <button onClick={() => navigate('/stock')} className="data-card-link">
                            Tumunu Gor <ArrowUpRight size={12} />
                        </button>
                    </div>
                    <div className="data-card-body p-0">
                        {(!stats?.recent_movements?.length) ? (
                            <div className="data-card-empty py-8">
                                <Package size={28} className="mx-auto mb-2 text-gray-200" />
                                <p className="text-sm text-gray-400">Henuz stok hareketi yok</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-50">
                                {stats.recent_movements.map((m, i) => (
                                    <div key={m.id || i} className="flex items-center gap-4 px-6 py-3.5 hover:bg-gray-50/50 transition-colors" data-testid={`movement-${i}`}>
                                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${m.movement_type === 'IN' ? 'bg-green-50' : 'bg-red-50'}`}>
                                            {m.movement_type === 'IN' ? <ArrowDownRight size={16} className="text-green-600" /> : <ArrowUpRight size={16} className="text-red-500" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-gray-900">{m.movement_type === 'IN' ? 'Stok Girisi' : 'Stok Cikisi'}</span>
                                                <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${m.movement_type === 'IN' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
                                                    {m.movement_type === 'IN' ? '+' : '-'}{m.lines_count} kalem
                                                </span>
                                            </div>
                                            <div className="text-xs text-gray-400 mt-0.5 truncate">
                                                {m.employee_name} {m.product_names ? `- ${m.product_names}` : ''}
                                            </div>
                                        </div>
                                        <div className="text-xs text-gray-400 shrink-0">
                                            {m.created_at ? new Date(m.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
}
